import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import client from "@/lib/api";

const SERIES = [
  { key: "views", label: "VIEWS", color: "#FFFFFF" },
  { key: "leads", label: "LEADS", color: "#FACC15" },
  { key: "conversions", label: "CONVERSIONS", color: "#22C55E" },
];

const Totals = ({ totals }) => (
  <div className="grid grid-cols-4 gap-2 text-center border-y border-white/10 py-3 mb-4" data-testid="funnel-stats-totals">
    {SERIES.map(s => (
      <div key={s.key}>
        <div className="text-xl font-display" style={{ color: s.color }} data-testid={`funnel-stats-total-${s.key}`}>{totals?.[s.key] ?? 0}</div>
        <div className="text-[9px] font-mono-k text-[#A1A1AA] tracking-widest-k">{s.label}</div>
      </div>
    ))}
    <div>
      <div className="text-xl font-display text-[#00E5FF]" data-testid="funnel-stats-total-rate">{totals?.conversion_rate ?? 0}%</div>
      <div className="text-[9px] font-mono-k text-[#A1A1AA] tracking-widest-k">LEAD RATE</div>
    </div>
  </div>
);

export const FunnelStatsModal = ({ funnel, onClose }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    client.get(`/funnels/${funnel.id}/stats?days=${days}`)
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Could not load stats"));
  }, [funnel.id, days]);

  const empty = data && !data.series.some(r => r.views || r.leads || r.conversions);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="funnel-stats-modal">
      <div className="w-full max-w-3xl bg-[#0A0D14] border border-white/15 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15]">— PERFORMANCE OVER TIME</div>
            <div className="font-display text-2xl">{funnel.name}</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="funnel-stats-close"><X size={20} /></button>
        </div>
        <div className="flex gap-1 mb-4">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} className={`k-btn text-xs ${days === d ? "primary" : ""}`} data-testid={`funnel-stats-range-${d}`}>{d} DAYS</button>
          ))}
        </div>
        {error && <div className="text-red-400 font-mono-k text-sm" data-testid="funnel-stats-error">{error}</div>}
        {!data && !error && <div className="h-64 flex items-center justify-center text-[#A1A1AA] font-mono-k text-sm">Loading…</div>}
        {data && (
          <>
            <Totals totals={data.totals} />
            <div className="h-64 relative" data-testid="funnel-stats-chart">
              {empty && <div className="absolute inset-0 flex items-center justify-center text-[#A1A1AA] font-mono-k text-xs z-10">No activity in this period yet — share your funnel link to start tracking.</div>}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#A1A1AA", fontSize: 10 }} tickFormatter={d => d.slice(5)} minTickGap={20} />
                  <YAxis allowDecimals={false} tick={{ fill: "#A1A1AA", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#0A0D14", border: "1px solid #ffffff30", fontSize: 12 }} />
                  {SERIES.map(s => (
                    <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} fill={s.color} fillOpacity={0.08} strokeWidth={2} dot={{ r: 2.5, fill: s.color }} activeDot={{ r: 5 }} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-3">Daily views and conversions are tracked from today onward; leads include your full history.</div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
